import { ReactNode } from 'react';
import { useIntl } from 'dibs-react-intl';
import { MultiSelect, SelectOption } from 'dibs-elements/exports/MultiSelect';
import { BasicSelect, BasicSelectOption } from 'dibs-elements/exports/BasicSelect';
import {
    BusinessItem,
    PrimaryBusinessFocus,
    Currency,
    VerticalType,
    VerticalSpecificField,
    businessTypeMap,
    verticalSpecificsMap,
    FURNITURE_VINTAGE,
    FURNITURE_CONTEMPORARY,
    FINE_ART,
    JEWELRY_ESTATE,
    JEWELRY_CONTEMPORARY,
    FASHION_VINTAGE,
    FASHION_CONTEMPORARY,
    MATERIALS,
    RESTORATION_AND_MODIFICATIONS,
    MEDIUM,
    MORE_SIZES_AND_EDITIONS,
    METALS,
    STONES,
    STONE_CUT,
    CUSTOMIZABLE,
} from '../form/SellerApplicationFormReducer';
import {
    yesNoMessages,
    businessFocusMessages,
    businessTypeMessages,
    fieldMessages,
    preferredLanguageMessages,
    getLabel,
    getValue,
    ascendingAlphabetical,
    AttributeType,
    flattenActiveAttributes,
} from './sellerApplicationOptionsHelper';
import classnames from 'classnames';

import dibsCss from 'dibs-css';

export const useGetYesNoOptions = (): SelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(yesNoMessages.yes),
            value: 'true',
        },
        {
            label: intl.formatMessage(yesNoMessages.no),
            value: 'false',
        },
    ];
};

export const useGetNumRangeOptions = (numRangeArr: readonly string[]): BasicSelectOption[] => {
    const options: BasicSelectOption[] = [];
    for (const numRange of numRangeArr) {
        options.push({
            // The replace here inserts spaces between the two numbers
            label: numRange.replace(/(\d*)-(\d*)/, '$1 - $2'),
            value: numRange,
        });
    }
    return options;
};

export const useGetItemPriceOptions = (
    selectedCurrency: Currency = 'USD'
): { label: string; value: string }[] => {
    const intl = useIntl();
    const priceRanges = [
        { to: 500 },
        { from: 500, to: 2000 },
        { from: 2000, to: 5000 },
        { from: 5000, to: 10000 },
        { from: 10000 },
    ];
    return priceRanges.map(range => ({
        label: getLabel(range, selectedCurrency, intl),
        value: getValue(range),
    }));
};

export const useGetBusinessFocusOptions = (): BasicSelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFurnitureOptionLabel),
            options: [
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFurnitureSelection1
                    ),
                    value: FURNITURE_VINTAGE,
                },
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFurnitureSelection2
                    ),
                    value: FURNITURE_CONTEMPORARY,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFashionOptionLabel),
            options: [
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusFashionSelection1),
                    value: FASHION_CONTEMPORARY,
                },
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusFashionSelection2),
                    value: FASHION_VINTAGE,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusJewelryOptionLabel),
            options: [
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusJewelrySelection1),
                    value: JEWELRY_ESTATE,
                },
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusJewelrySelection2),
                    value: JEWELRY_CONTEMPORARY,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFineArtOptionLabel),
            options: [
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFineArtOptionLabel
                    ),
                    value: FINE_ART,
                },
            ],
        },
    ];
};

export const useGetBusinessTypeOptions = ({
    selectedBusinessFocus,
}: {
    selectedBusinessFocus?: PrimaryBusinessFocus;
}): BasicSelectOption[] | undefined => {
    const intl = useIntl();
    if (!selectedBusinessFocus) {
        return undefined;
    }

    let label = '';
    const options = [];
    for (const businessType of businessTypeMap[selectedBusinessFocus]) {
        options.push({
            label: intl.formatMessage(businessTypeMessages[businessType]),
            value: businessType,
        });
    }
    switch (selectedBusinessFocus) {
        case FASHION_CONTEMPORARY:
        case FASHION_VINTAGE:
            label = intl.formatMessage(businessFocusMessages.businessFocusFashionOptionLabel);
            break;
        case FINE_ART:
            label = intl.formatMessage(businessFocusMessages.businessFocusFineArtOptionLabel);
            break;
        case FURNITURE_CONTEMPORARY:
        case FURNITURE_VINTAGE:
            label = intl.formatMessage(businessFocusMessages.businessFocusFurnitureOptionLabel);
            break;
        case JEWELRY_CONTEMPORARY:
        case JEWELRY_ESTATE:
            label = intl.formatMessage(businessFocusMessages.businessFocusJewelryOptionLabel);
            break;
    }
    return [{ label, options }];
};

type GetVerticalSpecificFieldArgs = {
    vertical?: VerticalType;
    field: VerticalSpecificField;
    options: SelectOption[];
    value?: string;
    values?: string[];
    setField: (name: keyof BusinessItem, value: string | string[] | boolean) => void;
    errorMessage: ReactNode;
};

export const useGetVerticalSpecificField = ({
    vertical,
    field,
    options,
    value,
    values = [],
    setField,
    errorMessage,
}: GetVerticalSpecificFieldArgs): ReactNode | null => {
    const intl = useIntl();
    if (!vertical) return null;

    const verticalComponents: { [field in VerticalSpecificField]: ReactNode } = {
        [MATERIALS]: (
            <BasicSelect
                dataTn="item-materials"
                name="item-materials"
                placeholder={intl.formatMessage(fieldMessages[MATERIALS])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => {
                    const val = e.target.value;
                    setField(MATERIALS, val);
                }}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [RESTORATION_AND_MODIFICATIONS]: (
            <BasicSelect
                dataTn="item-restoration-and-modification"
                name="item-restoration-and-modification"
                placeholder={intl.formatMessage(fieldMessages[RESTORATION_AND_MODIFICATIONS])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => setField(RESTORATION_AND_MODIFICATIONS, e.target.value === 'true')}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [MEDIUM]: (
            <BasicSelect
                dataTn="item-medium"
                name="item-medium"
                placeholder={intl.formatMessage(fieldMessages[MEDIUM])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => {
                    const val = e.target.value;
                    setField(MEDIUM, val);
                }}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [MORE_SIZES_AND_EDITIONS]: (
            <BasicSelect
                dataTn="item-more-sizes-and-editions"
                name="item-more-sizes-and-editions"
                placeholder={intl.formatMessage(fieldMessages[MORE_SIZES_AND_EDITIONS])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => setField(MORE_SIZES_AND_EDITIONS, e.target.value === 'true')}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [METALS]: (
            <MultiSelect
                dataTn="select-metal"
                name="select-metal"
                locale="en-us"
                values={values}
                options={options}
                hasAnimatedPlaceholder
                placeholder={intl.formatMessage(fieldMessages[METALS])}
                searchable
                noResultsPrompt="No Results Found"
                onChange={vals => {
                    const strValuesArr = [];
                    for (const val of vals) {
                        strValuesArr.push(val.value);
                    }
                    setField(METALS, strValuesArr);
                }}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [STONES]: (
            <MultiSelect
                dataTn="select-stone"
                name="select-stone"
                locale="en-us"
                values={values}
                options={options}
                hasAnimatedPlaceholder
                placeholder={intl.formatMessage(fieldMessages[STONES])}
                searchable
                noResultsPrompt="No Results Found"
                onChange={vals => {
                    const strValuesArr = [];
                    for (const val of vals) {
                        strValuesArr.push(val.value);
                    }
                    setField(STONES, strValuesArr);
                }}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [STONE_CUT]: (
            <BasicSelect
                dataTn="item-stone-cut"
                name="item-stone-cut"
                placeholder={intl.formatMessage(fieldMessages[STONE_CUT])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => {
                    const val = e.target.value;
                    setField(STONE_CUT, val);
                }}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
        [CUSTOMIZABLE]: (
            <BasicSelect
                dataTn="item-customizable"
                name="item-customizable"
                placeholder={intl.formatMessage(fieldMessages[CUSTOMIZABLE])}
                hasAnimatedPlaceholder
                options={options}
                value={value || ''}
                onChange={e => setField(CUSTOMIZABLE, e.target.value === 'true')}
                errorMessage={errorMessage}
                hasError={!!errorMessage}
            />
        ),
    };

    if (verticalSpecificsMap[vertical].fields.includes(field)) {
        return (
            <div
                className={classnames(
                    dibsCss.wFull,
                    dibsCss.mxAuto,
                    dibsCss.textCenter,
                    dibsCss.mbSmall
                )}
            >
                {verticalComponents[field]}
            </div>
        );
    }

    return null;
};

export const useGetAttributeOptions = <T extends ReadonlyArray<AttributeType> | null>(
    attributeArr?: T | null
): SelectOption[] => {
    if (!attributeArr) return [];

    const options: SelectOption[] = [];
    const flattenedAttributeArr = flattenActiveAttributes(attributeArr);
    for (const attributeObj of flattenedAttributeArr) {
        if (attributeObj?.label) {
            options.push({
                label: attributeObj.label,
                value: attributeObj.label,
            });
        }
    }
    return options.sort(ascendingAlphabetical(el => el.label));
};

export const useGetPreferredLanguageOptions = (): BasicSelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(preferredLanguageMessages.english),
            value: 'English',
        },
        {
            label: intl.formatMessage(preferredLanguageMessages.italian),
            value: 'Italian',
        },
    ];
};
